import Image from "next/image"
import { cn } from "@/lib/utils"
import { resolveBlogAuthor, type BlogAuthor, type BlogArticleAuthor } from "@/lib/blog-authors"

type BlogAuthorBylineProps = {
  date?: string
  author?: BlogAuthor
  authorKey?: BlogArticleAuthor
  /** Card footer vs article masthead */
  variant?: "compact" | "article"
  /** Glass-surface pages (/blog index) use --pg-* tokens instead of theme colors. */
  tone?: "default" | "glass"
  /** Show the booth mark circle (founder launch post on the journal index). */
  showAvatar?: boolean
  className?: string
}

function AuthorInitialsAvatar({
  initials,
  size,
  tone,
}: {
  initials: string
  size: "sm" | "md"
  tone: "default" | "glass"
}) {
  const dim = size === "sm" ? "w-7 h-7 text-[10px]" : "w-10 h-10 text-xs"
  return (
    <span
      className={cn(
        "shrink-0 rounded-full font-semibold inline-flex items-center justify-center",
        dim,
        tone === "default" && "bg-foreground text-background",
      )}
      style={
        tone === "glass"
          ? { background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }
          : undefined
      }
      aria-hidden
    >
      {initials}
    </span>
  )
}

/** BOOTH chair mark — gray icon only, no background circle. */
function BoothMarkAvatar({ size }: { size: "sm" | "md" }) {
  // A step under the old 11 / 13 — the mark sat as tall as the cap height of
  // the name beside it and read as a second word rather than a mark.
  const iconH = size === "sm" ? 9 : 11
  return (
    <Image
      src="/images/booth-icon-gray.png"
      alt=""
      width={iconH * 2}
      height={iconH}
      className="shrink-0 w-auto object-contain"
      style={{ height: iconH }}
      draggable={false}
      aria-hidden
    />
  )
}

export function BlogAuthorByline({
  date,
  author,
  authorKey,
  variant = "compact",
  tone = "default",
  showAvatar = false,
  className,
}: BlogAuthorBylineProps) {
  const resolved = author ?? resolveBlogAuthor(authorKey)
  const nameClass = tone === "glass" ? undefined : "text-foreground"
  const mutedClass = tone === "glass" ? undefined : "text-muted-foreground"
  const nameStyle = tone === "glass" ? { color: "var(--pg-fg)" } : undefined
  const mutedStyle = tone === "glass" ? { color: "var(--pg-muted)" } : undefined
  const faintStyle = tone === "glass" ? { color: "var(--pg-faint)" } : undefined
  const avatar =
    showAvatar && resolved.avatar === "booth" ? (
      <BoothMarkAvatar size="sm" />
    ) : showAvatar && resolved.avatar !== "booth" ? (
      <AuthorInitialsAvatar initials={resolved.initials} size="sm" tone={tone} />
    ) : null

  if (variant === "article") {
    return (
      <div className={cn("flex items-center gap-3", className)}>
        {showAvatar ? (
          resolved.avatar === "booth" ? (
            <BoothMarkAvatar size="md" />
          ) : (
            <AuthorInitialsAvatar initials={resolved.initials} size="md" tone={tone} />
          )
        ) : null}
        <div className="min-w-0 flex-1">
          <p className={cn("text-sm font-semibold leading-tight", nameClass)} style={nameStyle}>
            {resolved.name}
          </p>
          <p className={cn("text-[13px] leading-snug mt-0.5", mutedClass)} style={mutedStyle}>
            {resolved.role}
            {resolved.publication ? (
              <>
                <span className="mx-1.5" style={faintStyle} aria-hidden>
                  ·
                </span>
                <span
                  style={tone === "glass" ? mutedStyle : { color: undefined }}
                  className={tone === "default" ? "text-muted-foreground/80" : undefined}
                >
                  {resolved.publication}
                </span>
              </>
            ) : null}
          </p>
          {date ? (
            <p
              className={cn("text-xs mt-1 tabular-nums", tone === "default" && "text-muted-foreground/70")}
              style={tone === "glass" ? { ...mutedStyle, opacity: 0.85 } : undefined}
            >
              {date}
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex items-center gap-2 min-w-0", className)}>
      {avatar}
      <div className={cn("min-w-0 text-sm leading-tight", mutedClass)} style={mutedStyle}>
        <span className={cn("font-medium", nameClass)} style={nameStyle}>
          {resolved.name}
        </span>
        {date ? (
          <>
            <span className="mx-1.5" style={faintStyle} aria-hidden>
              ·
            </span>
            <span className="tabular-nums">{date}</span>
          </>
        ) : null}
      </div>
    </div>
  )
}
